import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ConfirmDeleteDialog } from "./ui/confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Plus, Pencil, Trash2, Sparkles, ChevronDown, ChevronUp, Info } from "lucide-react";
import { accountApi } from "../lib/api";

type CatalogContext = {
  id: string;
  simulatorId: string;
  clientAccountId: string | null;
  nameFr: string;
  nameEn: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  playerBriefMdFr?: string | null;
  playerBriefMdEn?: string | null;
  agentInstructionsMdFr?: string | null;
  agentInstructionsMdEn?: string | null;
  isSystem: boolean;
  isActive: boolean;
  orderIndex: number;
};

type Simulator = {
  id: string;
  name: string;
  isActive?: boolean;
  simulatorType?: string;
};

const BLUE = "#384872";

async function jsonFetch(input: string, init: RequestInit = {}) {
  const res = await fetch(input, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const body = await res.json(); msg = body.error || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export function AdminCatalogContextsTab() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";

  const [list, setList] = useState<CatalogContext[]>([]);
  const [simulators, setSimulators] = useState<Simulator[]>([]);
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CatalogContext | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<CatalogContext | null>(null);
  const [view, setView] = useState<"catalog" | "clients">("catalog");

  const reload = async () => {
    setLoading(true);
    try {
      const [ctxRes, agentsRes] = await Promise.all([
        // catalogView=1 : is_active GLOBAL du catalogue (pas la surcharge du compte de l'admin).
        jsonFetch("/api/account/contexts?catalogView=1"),
        fetch("/api/agents", { credentials: "same-origin" }).then((r) => r.json()).catch(() => ({ agents: [] })),
      ]);
      // L'API retourne tous les contextes accessibles à l'admin ; on ne garde que le catalogue (clientAccountId IS NULL)
      const catalog = (ctxRes.contexts || []).filter((c: CatalogContext) => c.clientAccountId === null);
      setList(catalog);
      const agents: Simulator[] = Array.isArray(agentsRes) ? agentsRes : agentsRes.agents || agentsRes.simulators || [];
      const filtered = agents.filter((s) => s.simulatorType === "training" && s.isActive !== false);
      setSimulators(filtered);
      if (!selectedSimulatorId && filtered.length > 0) setSelectedSimulatorId(filtered[0]!.id);
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (data: Partial<CatalogContext>) => {
    try {
      if (editing?.id) {
        const { context } = await jsonFetch(`/api/admin/contexts/catalog/${editing.id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        });
        setList((prev) => prev.map((c) => (c.id === context.id ? context : c)));
      } else {
        if (!data.simulatorId) { alert(t("adminCatalog.selectSimulatorRequired")); return; }
        const { context } = await jsonFetch("/api/admin/contexts/catalog", {
          method: "POST",
          body: JSON.stringify(data),
        });
        setList((prev) => [...prev, context]);
      }
      setEditing(null);
      setCreating(false);
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorSaving"));
    }
  };

  const remove = async (c: CatalogContext) => {
    try {
      await jsonFetch(`/api/admin/contexts/catalog/${c.id}`, { method: "DELETE" });
      setList((prev) => prev.filter((x) => x.id !== c.id));
      setConfirmDelete(null);
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorDeleting"));
    }
  };

  const toggleActive = async (c: CatalogContext) => {
    try {
      const { context } = await jsonFetch(`/api/admin/contexts/catalog/${c.id}/toggle`, {
        method: "PUT",
        body: JSON.stringify({ isActive: !c.isActive }),
      });
      setList((prev) => prev.map((x) => (x.id === context.id ? context : x)));
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorToggle"));
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">{t("adminCatalog.loading")}</p>;

  const filtered = selectedSimulatorId
    ? list.filter((c) => c.simulatorId === selectedSimulatorId).sort((a, b) => a.orderIndex - b.orderIndex)
    : [];
  const selectedSim = simulators.find((s) => s.id === selectedSimulatorId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: "Sora, sans-serif", color: BLUE }}>
            {t("adminCatalog.title")}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t("adminCatalog.subtitle")}
          </p>
        </div>
        {view === "catalog" && selectedSim && (
          <Button onClick={() => setCreating(true)} size="sm" className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white">
            <Plus className="h-4 w-4" />
            {t("adminCatalog.newCatalogContext")}
          </Button>
        )}
      </div>

      {/* Segment : catalogue Kestio vs contextes custom clients */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {(["catalog", "clients"] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${view === v ? "bg-[#384872] text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            {v === "catalog" ? t("contexts.catalogTab") : t("contexts.clientContextsTab")}
          </button>
        ))}
      </div>

      {view === "clients" && <AdminClientContextsList isFr={isFr} />}

      {view === "catalog" && (<>
      {/* Filtre simulateur */}
      <div className="max-w-sm">
        <Label className="text-xs text-slate-500">{t("adminCatalog.simulator")}</Label>
        <Select value={selectedSimulatorId} onValueChange={setSelectedSimulatorId}>
          <SelectTrigger><SelectValue placeholder={t("adminCatalog.chooseSimulator")} /></SelectTrigger>
          <SelectContent>
            {simulators.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Aide : le nouveau contexte est rattaché au simulateur filtré ci-dessus */}
      <p className="text-xs text-slate-500 flex items-start gap-1.5 -mt-1">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>{t("adminCatalog.createHint", { button: t("adminCatalog.newCatalogContext") })}</span>
      </p>

      {/* Liste */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-slate-500">{t("adminCatalog.emptyForSimulator")}</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => {
            const filledMd = [c.playerBriefMdFr, c.playerBriefMdEn, c.agentInstructionsMdFr, c.agentInstructionsMdEn].filter(Boolean).length;
            return (
              <Card key={c.id} className={c.isActive ? "" : "opacity-60"}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium" style={{ color: BLUE }}>
                        {isFr ? (c.nameFr || c.nameEn) : (c.nameEn || c.nameFr)}
                      </span>
                      <Badge variant="outline" className="text-xs">{t("adminCatalog.mdBadge", { count: filledMd })}</Badge>
                      {!c.isActive && <Badge variant="secondary" className="text-xs">{t("adminCatalog.inactiveBadge")}</Badge>}
                    </div>
                    {(c.descriptionFr || c.descriptionEn) && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                        {isFr ? (c.descriptionFr || c.descriptionEn) : (c.descriptionEn || c.descriptionFr)}
                      </p>
                    )}
                  </div>
                  <Switch checked={c.isActive} onCheckedChange={() => toggleActive(c)} aria-label={t("adminCatalog.activateAria")} />
                  <Button size="icon" variant="ghost" onClick={() => setEditing(c)} aria-label={t("adminCatalog.editAria")}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(c)} aria-label={t("adminCatalog.deleteAria")}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {(editing || creating) && (
        <CatalogContextEditor
          ctx={editing}
          simulatorId={editing?.simulatorId || selectedSimulatorId}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteDialog
          open={!!confirmDelete}
          onOpenChange={(o: boolean) => !o && setConfirmDelete(null)}
          title={t("adminCatalog.deleteTitle")}
          description={t("adminCatalog.deleteDescription", { name: isFr ? confirmDelete.nameFr : confirmDelete.nameEn })}
          onConfirm={() => remove(confirmDelete)}
        />
      )}
      </>)}
    </div>
  );
}

function CatalogContextEditor({
  ctx,
  simulatorId,
  onClose,
  onSave,
}: {
  ctx: CatalogContext | null;
  simulatorId: string;
  onClose: () => void;
  onSave: (data: Partial<CatalogContext>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [nameFr, setNameFr] = useState(ctx?.nameFr || "");
  const [nameEn, setNameEn] = useState(ctx?.nameEn || "");
  const [descriptionFr, setDescriptionFr] = useState(ctx?.descriptionFr || "");
  const [descriptionEn, setDescriptionEn] = useState(ctx?.descriptionEn || "");
  const [playerBriefMdFr, setPlayerBriefMdFr] = useState(ctx?.playerBriefMdFr || "");
  const [playerBriefMdEn, setPlayerBriefMdEn] = useState(ctx?.playerBriefMdEn || "");
  const [agentInstructionsMdFr, setAgentInstructionsMdFr] = useState(ctx?.agentInstructionsMdFr || "");
  const [agentInstructionsMdEn, setAgentInstructionsMdEn] = useState(ctx?.agentInstructionsMdEn || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!nameFr.trim() || !nameEn.trim()) {
      alert(t("adminCatalog.namesRequired"));
      return;
    }
    setSaving(true);
    try {
      await onSave({
        simulatorId: ctx?.simulatorId || simulatorId,
        nameFr,
        nameEn,
        descriptionFr,
        descriptionEn,
        playerBriefMdFr,
        playerBriefMdEn,
        agentInstructionsMdFr,
        agentInstructionsMdEn,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ctx ? t("adminCatalog.editorTitleEdit") : t("adminCatalog.editorTitleCreate")}</DialogTitle>
          <DialogDescription>
            {t("adminCatalog.editorDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Nom */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("adminCatalog.nameFr")}</Label>
              <Input value={nameFr} onChange={(e) => setNameFr(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t("adminCatalog.nameEn")}</Label>
              <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
            </div>
          </div>

          {/* Description courte */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("adminCatalog.shortDescFr")}</Label>
              <textarea
                value={descriptionFr}
                onChange={(e) => setDescriptionFr(e.target.value)}
                className="w-full text-sm border rounded p-2 min-h-[60px]"
              />
            </div>
            <div>
              <Label className="text-xs">{t("adminCatalog.shortDescEn")}</Label>
              <textarea
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                className="w-full text-sm border rounded p-2 min-h-[60px]"
              />
            </div>
          </div>

          {/* Brief joueur (Markdown) */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium" style={{ color: BLUE }}>{t("adminCatalog.playerBriefTitle")}</p>
            <p className="text-xs text-slate-500 mb-2">
              {t("adminCatalog.playerBriefSubtitle")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("adminCatalog.fr")}</Label>
                <textarea
                  value={playerBriefMdFr}
                  onChange={(e) => setPlayerBriefMdFr(e.target.value)}
                  className="w-full text-sm font-mono border rounded p-2 min-h-[140px]"
                />
              </div>
              <div>
                <Label className="text-xs">{t("adminCatalog.en")}</Label>
                <textarea
                  value={playerBriefMdEn}
                  onChange={(e) => setPlayerBriefMdEn(e.target.value)}
                  className="w-full text-sm font-mono border rounded p-2 min-h-[140px]"
                />
              </div>
            </div>
          </div>

          {/* Instructions agent (Markdown) */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium" style={{ color: BLUE }}>{t("adminCatalog.agentInstructionsTitle")}</p>
            <p className="text-xs text-slate-500 mb-2">
              {t("adminCatalog.agentInstructionsSubtitle")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("adminCatalog.fr")}</Label>
                <textarea
                  value={agentInstructionsMdFr}
                  onChange={(e) => setAgentInstructionsMdFr(e.target.value)}
                  className="w-full text-sm font-mono border rounded p-2 min-h-[200px]"
                />
              </div>
              <div>
                <Label className="text-xs">{t("adminCatalog.en")}</Label>
                <textarea
                  value={agentInstructionsMdEn}
                  onChange={(e) => setAgentInstructionsMdEn(e.target.value)}
                  className="w-full text-sm font-mono border rounded p-2 min-h-[200px]"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("adminCatalog.cancel")}</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#384872] hover:bg-[#2d3a5c] text-white">
            {saving ? t("adminCatalog.saving") : t("adminCatalog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Vue globale des contextes CUSTOM des clients (ADM/COO) — voir/éditer/régénérer.
// =============================================================================
type ClientContext = CatalogContext & { accountName?: string; simulatorName?: string };

function AdminClientContextsList({ isFr }: { isFr: boolean }) {
  const { t } = useTranslation();
  const [list, setList] = useState<ClientContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const reload = async () => {
    setLoading(true);
    try {
      const rows = await accountApi.adminListClientContexts();
      setList(rows || []);
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorLoading"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openEdit = (c: ClientContext) => {
    if (expandedId === c.id) { setExpandedId(null); return; }
    setExpandedId(c.id);
    setDraft({
      playerBriefMdFr: c.playerBriefMdFr || "",
      playerBriefMdEn: c.playerBriefMdEn || "",
      agentInstructionsMdFr: c.agentInstructionsMdFr || "",
      agentInstructionsMdEn: c.agentInstructionsMdEn || "",
    });
  };

  const saveMd = async (c: ClientContext) => {
    setBusyId(c.id);
    try {
      const updated = await accountApi.updateContext(c.id, draft, c.clientAccountId || undefined);
      setList((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
      setExpandedId(null);
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorSaving"));
    } finally {
      setBusyId(null);
    }
  };

  const regenerate = async (c: ClientContext) => {
    setBusyId(c.id);
    try {
      await accountApi.adminRegenerateContextBrief(c.id);
      alert(t("contexts.regenerateStarted"));
      setTimeout(reload, 6000);
    } catch (err: any) {
      alert(err.message || t("adminCatalog.errorSaving"));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground py-6">…</p>;
  if (list.length === 0) return <p className="text-sm text-muted-foreground py-6">{t("contexts.clientContextsEmpty")}</p>;

  // Groupé par compte
  const byAccount = new Map<string, ClientContext[]>();
  for (const c of list) {
    const key = c.accountName || "—";
    if (!byAccount.has(key)) byAccount.set(key, []);
    byAccount.get(key)!.push(c);
  }

  const mdField = "w-full min-h-[120px] px-3 py-2 rounded-md border border-input bg-background text-sm font-mono resize-y";

  return (
    <div className="space-y-5">
      {[...byAccount.entries()].map(([account, ctxs]) => (
        <div key={account} className="space-y-2">
          <h3 className="text-sm font-semibold" style={{ color: BLUE }}>{account} <span className="text-muted-foreground font-normal">({ctxs.length})</span></h3>
          {ctxs.map((c) => {
            const briefOk = !!(c.playerBriefMdFr || c.playerBriefMdEn);
            const instrOk = !!(c.agentInstructionsMdFr || c.agentInstructionsMdEn);
            const expanded = expandedId === c.id;
            return (
              <Card key={c.id}>
                <CardContent className="!py-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{isFr ? (c.nameFr || c.nameEn) : (c.nameEn || c.nameFr)}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.simulatorName} · {isFr ? (c.descriptionFr || c.descriptionEn) : (c.descriptionEn || c.descriptionFr)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={briefOk ? "default" : "secondary"} className="text-xs">{t("contexts.briefFilled")}{briefOk ? " ✓" : " —"}</Badge>
                      <Badge variant={instrOk ? "default" : "secondary"} className="text-xs">{t("contexts.instrFilled")}{instrOk ? " ✓" : " —"}</Badge>
                      <Button variant="outline" size="sm" type="button" disabled={busyId === c.id} onClick={() => regenerate(c)}>
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />{t("contexts.regenerateBrief")}
                      </Button>
                      <Button variant="ghost" size="sm" type="button" onClick={() => openEdit(c)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />{expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>{t("contexts.playerBriefLabel", { lang: "FR" })}</Label>
                          <textarea className={mdField} value={draft.playerBriefMdFr || ""} onChange={(e) => setDraft((d) => ({ ...d, playerBriefMdFr: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <Label>{t("contexts.playerBriefLabel", { lang: "EN" })}</Label>
                          <textarea className={mdField} value={draft.playerBriefMdEn || ""} onChange={(e) => setDraft((d) => ({ ...d, playerBriefMdEn: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <Label>{t("contexts.agentInstructionsLabel", { lang: "FR" })}</Label>
                          <textarea className={mdField} value={draft.agentInstructionsMdFr || ""} onChange={(e) => setDraft((d) => ({ ...d, agentInstructionsMdFr: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <Label>{t("contexts.agentInstructionsLabel", { lang: "EN" })}</Label>
                          <textarea className={mdField} value={draft.agentInstructionsMdEn || ""} onChange={(e) => setDraft((d) => ({ ...d, agentInstructionsMdEn: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" type="button" onClick={() => setExpandedId(null)}>{t("common.cancel")}</Button>
                        <Button size="sm" type="button" disabled={busyId === c.id} className="bg-[#384872] hover:bg-[#2d3a5c]" onClick={() => saveMd(c)}>{t("common.save")}</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ))}
    </div>
  );
}
